import React, { useState, useCallback } from 'react';
import { Box, Typography, Grid, Paper, IconButton } from '@mui/material';
// import { useDropzone } from 'react-dropzone';
import { Button } from '@voicecare.ai/ui-library';
import { useOutletContext, useNavigate } from 'react-router-dom';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'; // Generic file icon
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile'; // Upload icon
// --- Import SheetJS (xlsx) library AFTER installing it --- 
import * as XLSX from 'xlsx'; // <-- Uncommented import

const FileUploadStep = () => {
  const { themeMode, currentTheme, formState } = useOutletContext();
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false); // Add processing state

  const currentState = formState || {};

  // Commented out dropzone related code
  /*const onDrop = useCallback((acceptedFiles) => {
    setUploadedFiles(prevFiles => {
      const newFiles = acceptedFiles.filter(newFile => 
        !prevFiles.some(prevFile => 
          prevFile.name === newFile.name && prevFile.size === newFile.size
        )
      );
      return [...prevFiles, ...newFiles];
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'application/vnd.ms-excel.template.macroEnabled.12': ['.xltm'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.template': ['.xltx']
    }
  });*/

  // New file input handler
  const handleFileInput = (event) => {
    const files = Array.from(event.target.files);
    setUploadedFiles(prevFiles => {
      const newFiles = files.filter(newFile => 
        !prevFiles.some(prevFile => 
          prevFile.name === newFile.name && prevFile.size === newFile.size
        )
      );
      return [...prevFiles, ...newFiles];
    });
  };

  const removeFile = (fileToRemove) => {
    setUploadedFiles(prevFiles => prevFiles.filter(file => file !== fileToRemove));
  };

  const clearAll = () => {
    setUploadedFiles([]);
  };

  const handleBack = () => {
    // Navigate back to the question selection step, passing the current state
    navigate(`/call-automation-v2/question-selection`, { state: currentState });
  };

  // --- Updated handleSubmit to read files and log JSON --- 
  const handleSubmit = async () => {
    if (uploadedFiles.length === 0) return;
    
    setIsProcessing(true); // Indicate processing started
    console.log("Starting file processing...");
    console.log("Current State:", currentState);

    const fileReadPromises = uploadedFiles.map(file => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
          try {
            const binaryString = event.target.result;
            
            // --- !!! IMPORTANT: Use SheetJS (xlsx) here !!! ---
            // 1. Ensure you have imported XLSX: import * as XLSX from 'xlsx';
            // 2. Read the workbook
            const workbook = XLSX.read(binaryString, { type: 'binary' }); // <-- Uncommented
            // 3. Get the first sheet name
            const sheetName = workbook.SheetNames[0]; // <-- Uncommented
            // 4. Get the worksheet
            const worksheet = workbook.Sheets[sheetName]; // <-- Uncommented
            // 5. Convert sheet to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet); // <-- Uncommented
            
            // --- Placeholder until SheetJS is implemented ---
            // const jsonData = [{ placeholder: `JSON data for ${file.name} would be here` }]; // <-- DELETED placeholder

            console.log(`--- JSON Data for: ${file.name} ---`);
            console.log(JSON.stringify(jsonData, null, 2)); // Pretty print JSON
            console.log(`------------------------------------`);
            
            resolve({ fileName: file.name, data: jsonData }); // Resolve promise with file data
          } catch (error) {
            console.error(`Error processing file ${file.name}:`, error);
            reject({ fileName: file.name, error: error }); // Reject promise on error
          }
        };

        reader.onerror = (error) => {
            console.error(`Error reading file ${file.name}:`, error);
            reject({ fileName: file.name, error: `File read error` });
        };

        // Read the file as a binary string (suitable for SheetJS)
        reader.readAsBinaryString(file); 
      });
    });

    try {
      const results = await Promise.all(fileReadPromises);
      console.log("All files processed.");
      // results is an array: [{ fileName: 'file1.xlsx', data: [...] }, { fileName: 'file2.xlsx', error: ... }]
      
      // --- Prepare state for next step --- 
      const nextState = { 
          ...currentState, 
          // You might want to pass the actual processed data or just filenames/references
          processedFilesInfo: results.map(r => ({ fileName: r.fileName, hasData: !!r.data, error: r.error }))
      };

      // --- Navigate to the next step --- 
      // navigate(`/call-automation-v2/summary`, { state: nextState }); // <-- Commented out for debugging

    } catch (error) {
        console.error("An error occurred during file processing:", error);
        // Handle overall error if needed (e.g., show a message to the user)
    } finally {
        setIsProcessing(false); // Indicate processing finished
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  // Common text style
  const textStyle = { 
      color: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
      textAlign: 'center'
  };

  return (
    <Box 
        sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            height: '100%', // Take full height of the right panel
            padding: 3, 
            backgroundColor: themeMode === 'dark' ? '#2a2a3f' : '#f4f6f8', // Match background style
            color: themeMode === 'dark' ? '#ffffff' : currentTheme?.callAutomationPage?.textColor 
        }}
    >
        {/* Main Content Area (Dropzone or Previews) */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {uploadedFiles.length === 0 ? (
                // Modified Upload Area
                <Box 
                    sx={{ 
                        border: `2px dashed ${themeMode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`,
                        borderRadius: 2,
                        padding: 5,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '80%',
                        maxWidth: '600px',
                        minHeight: '250px',
                        backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                    }}
                >
                    <input
                        type="file"
                        accept=".xls,.xlsx,.xlsm,.xltm,.xltx"
                        onChange={handleFileInput}
                        style={{ display: 'none' }}
                        id="file-input"
                        multiple
                    />
                    <InsertDriveFileIcon sx={{ fontSize: 60, color: '#ff4e3a', mb: 2 }} />
                    <Typography sx={textStyle} gutterBottom>
                        Select excel (.xls, .xlsx, xlms, xlt) files to upload
                    </Typography>
                    <Typography sx={{ ...textStyle, my: 1 }}>
                        Or
                    </Typography>
                    <Button 
                        label="Upload Files" 
                        icon={<UploadFileIcon />} 
                        sx={{ mt: 1, backgroundColor: '#ff4e3a', '&:hover': { backgroundColor: '#e6402f' } }} 
                        onClick={() => document.getElementById('file-input').click()}
                    /> 
                </Box>
            ) : (
                // File Preview Grid View
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                    <Grid container spacing={2} justifyContent="center">
                        {uploadedFiles.map((file, index) => (
                            <Grid item key={index} xs={12} sm={6} md={4} lg={3}> {/* Adjust grid sizing */} 
                                <Paper 
                                    elevation={2} 
                                    sx={{ 
                                        padding: 1.5, 
                                        position: 'relative', 
                                        textAlign: 'center', 
                                        backgroundColor: themeMode === 'dark' ? '#3e3c56' : '#ffffff'
                                    }}
                                >
                                    <IconButton 
                                        size="small"
                                        onClick={() => removeFile(file)} 
                                        sx={{ position: 'absolute', top: 4, right: 4, color: '#ff4e3a' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                    <InsertDriveFileIcon sx={{ fontSize: 50, color: '#ff4e3a', mb: 1 }} />
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all', color: themeMode === 'dark' ? '#fff' : '#000' }} noWrap>
                                        {file.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: themeMode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>
                                        {formatBytes(file.size)}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                        {/* Upload More Button */}    
                        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box 
                                sx={{
                                    border: `2px dashed ${themeMode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`,
                                    borderRadius: 2,
                                    padding: 1.5,
                                    height: '100%',
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    textAlign: 'center', 
                                    cursor: 'pointer', 
                                    minHeight: '140px',
                                    backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                                    width: '100%'
                                }}
                                onClick={() => document.getElementById('file-input').click()}
                            >
                                <input
                                    type="file"
                                    accept=".xls,.xlsx,.xlsm,.xltm,.xltx"
                                    onChange={handleFileInput}
                                    style={{ display: 'none' }}
                                    id="file-input"
                                    multiple
                                />
                                <UploadFileIcon sx={{ fontSize: 40, color: '#ff4e3a' }} />
                                <Typography variant="body2" sx={{ mt: 1, color: themeMode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>
                                    Upload More
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    <Typography sx={{ ...textStyle, mt: 3 }}>
                        Select excel files (.xls, .xlsx, xlms, xlt) to upload
                    </Typography>
                </Box>
            )}
        </Box>

        {/* Bottom Action Buttons */} 
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Button 
              label="Back" 
              onClick={handleBack}
              disabled={isProcessing} // Disable while processing
            />
            <Box>
                {uploadedFiles.length > 0 && (
                    <Button 
                        label="Clear All" 
                        onClick={clearAll}
                        variant="outlined" // Or appropriate style
                        sx={{ mr: 2 }} // Add margin if needed
                        disabled={isProcessing} // Disable while processing
                    />
                )}
                <Button 
                    label={isProcessing ? "Processing..." : "Submit"} // Change label during processing
                    onClick={handleSubmit}
                    disabled={uploadedFiles.length === 0 || isProcessing} // Disable if no files or processing
                />
            </Box>
        </Box>
    </Box>
  );
};

export default FileUploadStep; 